import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'ui';
import { FarmInfrastructures } from 'types/FarmData';
import { OpenHoursInput, normalizeOpenHours } from 'utils/timeHelpers';
import { haversineKm, parsePoint } from 'utils/geoHelpers';
import { useUserLocation } from 'utils/useUserLocation';
import { getOpenStatus, hasAnyOpenHours, isOpenNow } from '../utils/openStatus';
import FarmPlaceModal, { FarmPlaceDetails } from './FarmPlaceModal';
import {
  RowIcon,
  SectionIcon,
  SectionNote,
  SectionToolbar,
  StatusPill,
  ViewButton,
} from './FarmSection';

type PlaceFilter = 'all' | 'open' | 'nearest';

interface FarmPlacesProps {
  infrastructures: FarmInfrastructures[];
  fallbackOpenHours?: OpenHoursInput;
  farmName?: string;
  phone?: string | null;
  email?: string | null;
}

const addressOf = (infra: FarmInfrastructures) =>
  infra.address_pak || infra.address_reg || '';

const iconOf = (infra: FarmInfrastructures) => {
  if (infra.is_address_unit) return 'farm-place-farm';

  return /tirg/i.test(infra.structural_type ?? '')
    ? 'farm-place-market'
    : 'farm-place-shop';
};

const FarmPlaces = ({
  infrastructures,
  fallbackOpenHours,
  farmName,
  phone,
  email,
}: FarmPlacesProps) => {
  const intl = useIntl();
  const { location, status, requestLocation } = useUserLocation();
  const [filter, setFilter] = useState<PlaceFilter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const places = useMemo(
    () =>
      infrastructures.map((infra) => {
        const hours = normalizeOpenHours(infra.open_hours ?? fallbackOpenHours);
        const point = parsePoint(infra.maps_api_point);

        return {
          infra,
          hours,
          point,
          status: getOpenStatus(hours),
          distanceKm:
            location && point ? Math.round(haversineKm(location, point)) : null,
          tag: infra.is_address_unit
            ? intl.formatMessage({ id: 'farm_view.place_type_farm' })
            : infra.structural_type?.trim() || null,
          hasDetails: !!addressOf(infra) || !!point || hasAnyOpenHours(hours),
        };
      }),
    [infrastructures, fallbackOpenHours, location, intl]
  );

  const visible = useMemo(() => {
    if (filter === 'open') {
      return places.filter((place) => isOpenNow(place.status));
    }

    if (filter === 'nearest') {
      return [...places].sort(
        (a, b) =>
          (a.distanceKm ?? Number.POSITIVE_INFINITY) -
          (b.distanceKm ?? Number.POSITIVE_INFINITY)
      );
    }

    return places;
  }, [places, filter]);

  const selected = places.find((place) => place.infra.id === selectedId);
  const selectedDetails: FarmPlaceDetails | null = selected
    ? {
        key: String(selected.infra.id),
        title: selected.infra.object_name,
        subtitle: farmName,
        tag: selected.tag,
        tagIcon: iconOf(selected.infra),
        point: selected.point,
        address: addressOf(selected.infra),
        phone,
        email,
        days: selected.hours,
        note: selected.infra.description,
      }
    : null;

  const handleFilterChange = (next: PlaceFilter) => {
    if (next === 'nearest' && !location) requestLocation();
    setFilter(next);
  };

  const canRequestLocation =
    !location && status !== 'unsupported' && status !== 'requesting';

  return (
    <div className={'farm-section farm-places'}>
      <SectionToolbar
        title={intl.formatMessage({ id: 'farm_view.places_title' })}
        count={places.length}
        filters={[
          {
            key: 'all',
            label: intl.formatMessage({ id: 'farm_view.filter_all_places' }),
          },
          {
            key: 'open',
            label: intl.formatMessage({ id: 'farm_view.filter_open_now' }),
          },
          {
            key: 'nearest',
            label: intl.formatMessage({ id: 'farm_view.filter_nearest' }),
          },
        ]}
        activeFilter={filter}
        onFilterChange={handleFilterChange}
      />

      <div className={'farm-table'}>
        <div className={'farm-table-head'}>
          <span className={'farm-col-place'}>
            {intl.formatMessage({ id: 'farm_view.col_place' })}
          </span>
          <span className={'farm-col-address'}>
            {intl.formatMessage({ id: 'farm_view.col_address' })}
          </span>
          <span className={'farm-col-status'}>
            {intl.formatMessage({ id: 'farm_view.col_today' })}
          </span>
          <span className={'farm-col-distance'}>
            {intl.formatMessage({ id: 'farm_view.col_distance' })}
          </span>
          <span className={'farm-col-action'} />
        </div>

        {visible.map((place) => {
          const { infra } = place;
          const isSelected = infra.id === selectedId;
          const address = addressOf(infra);

          return (
            <div
              key={infra.id}
              className={`farm-table-row${isSelected ? ' selected' : ''}`}
            >
              <div className={'farm-row-top'}>
                <div className={'farm-col-place'}>
                  <SectionIcon name={iconOf(infra)} />
                  <span className={'farm-row-title'}>
                    <span className={'farm-row-name'}>{infra.object_name}</span>
                    {!!place.tag && (
                      <span className={'farm-row-tag'} title={place.tag}>
                        {place.tag}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className={'farm-col-status'}>
                {place.status && <StatusPill status={place.status} />}
              </div>
              <div className={'farm-col-address'}>
                {!!address && (
                  <>
                    <RowIcon name={'farm-pin'} />
                    <span>{address}</span>
                  </>
                )}
              </div>
              <div className={'farm-row-footer'}>
                <div
                  className={`farm-col-distance${place.distanceKm === null ? ' empty' : ''}`}
                >
                  <RowIcon name={'farm-distance'} />
                  <span className={'farm-row-distance'}>
                    {place.distanceKm !== null ? `${place.distanceKm} km` : '—'}
                  </span>
                </div>
                <div className={'farm-col-action'}>
                  {place.hasDetails && (
                    <ViewButton
                      active={isSelected}
                      onClick={() =>
                        setSelectedId(isSelected ? null : infra.id)
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!visible.length && (
          <p className={'farm-table-empty'}>
            {intl.formatMessage({ id: 'farm_view.no_open_places' })}
          </p>
        )}
      </div>

      <FarmPlaceModal
        place={selectedDetails}
        onClose={() => setSelectedId(null)}
      />

      <SectionNote>
        {intl.formatMessage({
          id: location ? 'farm_view.places_note' : 'farm_view.hours_note',
        })}
        {canRequestLocation && (
          <>
            {' '}
            <Button
              type={'link'}
              className={'farm-section-note-link'}
              label={intl.formatMessage({ id: 'farm_view.enable_location' })}
              onClick={requestLocation}
            />
          </>
        )}
      </SectionNote>
    </div>
  );
};

export default FarmPlaces;
